import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PlanBadge, StatusBadge, DetailRow, useConfirmModal } from "./adminShared";

const fmtDateTime = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtDateShort = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const fmtBRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const CLIENTS_PAGE_SIZE = 20;

// YYYY-MM-DD (fuso local) para <input type="date">
const toDateInput = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const ACTION_LABELS = {
    "tenant.created": "Tenant criado",
    "tenant.suspended": "Tenant suspenso",
    "tenant.activated": "Tenant ativado",
    "tenant.deleted": "Tenant excluído",
    "tenant.restored": "Tenant reabilitado",
    "tenant.owner_password_reset": "Senha do dono resetada",
    "tenant.payment_registered": "Pagamento registrado",
    "admin.tenant_plan_changed": "Plano alterado (admin)",
    "admin.tenant_validity_changed": "Validade alterada (admin)",
    "whatsapp.disconnected": "WhatsApp desconectado",
    "whatsapp.session_cleared": "Sessão do WhatsApp limpa",
    "booking.confirmed": "Agendamento confirmado",
    "booking.refused": "Agendamento recusado",
    "waitlist.notified": "Cliente notificado (lista de espera)",
    "waitlist.scheduled": "Agendamento via lista de espera",
    "subscription.created": "Assinatura criada",
    "subscription.renewed": "Assinatura renovada",
    "subscription.cancelled": "Assinatura cancelada",
    "subscription.plan_changed": "Plano de assinatura alterado",
};
const humanizeAction = (action) => ACTION_LABELS[action] || (action || "").replace(/[._]/g, " ");

const STATUS_OPTIONS = [
    { id: "active", label: "Ativo" },
    { id: "trial", label: "Trial" },
    { id: "suspended", label: "Suspenso" },
    { id: "cancelled", label: "Cancelado" },
];

/**
 * AdminTenantDetail — tela cheia com detalhes de um tenant, em camadas
 * empilhadas verticalmente (Informações Gerais, Estatísticas, Configurações,
 * Atividade Recente). Substitui o antigo drawer lateral.
 */
const AdminTenantDetail = ({ tenantId, tenant, onBack, onTenantUpdated }) => {
    const [localTenant, setLocalTenant] = useState(tenant);
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState(null);
    const [plans, setPlans] = useState([]);
    const [savingPlan, setSavingPlan] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);
    const [savingValidity, setSavingValidity] = useState(false);
    const [resettingPwd, setResettingPwd] = useState(false);
    const [resetResult, setResetResult] = useState(null);
    const { requestConfirm, ConfirmModalElement } = useConfirmModal();

    // Layer 5 — Clientes do Salão (lista paginada, busca com debounce)
    const [clientSearch, setClientSearch] = useState("");
    const [clientPage, setClientPage] = useState(1);
    const [clientData, setClientData] = useState(null); // { items, total, page, page_size }
    const [clientsLoading, setClientsLoading] = useState(true);

    useEffect(() => { setLocalTenant(tenant); }, [tenant]);

    // Reinicia a paginação sempre que o termo de busca muda
    useEffect(() => { setClientPage(1); }, [clientSearch]);

    useEffect(() => {
        let cancelled = false;
        setClientsLoading(true);
        const t = setTimeout(async () => {
            try {
                const qs = new URLSearchParams({ page: String(clientPage), page_size: String(CLIENTS_PAGE_SIZE) });
                if (clientSearch.trim()) qs.set("search", clientSearch.trim());
                const res = await api.get(`/admin/tenants/${tenantId}/clients?${qs.toString()}`);
                if (!cancelled) setClientData(res);
            } catch (_) {
                if (!cancelled) setClientData({ items: [], total: 0, page: 1, page_size: CLIENTS_PAGE_SIZE });
            } finally {
                if (!cancelled) setClientsLoading(false);
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
    }, [tenantId, clientSearch, clientPage]);

    const load = useCallback(async () => {
        try {
            const [s, a, p] = await Promise.all([
                api.get(`/admin/tenants/${tenantId}/stats`).catch(() => null),
                api.get(`/admin/tenants/${tenantId}/activity`).catch(() => null),
                api.get("/admin/plans").catch(() => null),
            ]);
            if (s) setStats(s);
            if (a) setActivity(a);
            if (Array.isArray(p)) setPlans(p);
        } catch (_) { /* silent */ }
    }, [tenantId]);

    useEffect(() => { load(); }, [load]);

    if (!localTenant) {
        return (
            <div data-testid="admin-tenant-detail" className="space-y-6">
                <BackButton onBack={onBack} />
                <div className="py-16 text-center text-slate-500">
                    <i className="fa-solid fa-triangle-exclamation text-3xl mb-3 opacity-40"></i>
                    <p>Tenant não encontrado.</p>
                </div>
            </div>
        );
    }

    const handlePlanChange = async (e) => {
        const newPlan = e.target.value;
        setSavingPlan(true);
        try {
            const updated = await api.patch(`/admin/tenants/${tenantId}/plan`, { plan: newPlan });
            setLocalTenant((t) => ({ ...t, ...(updated || {}), plan: (updated && updated.plan) || newPlan }));
            toast.success("Plano atualizado", { description: `Novo plano: ${(updated && updated.plan) || newPlan}` });
            onTenantUpdated?.();
        } catch (err) {
            toast.error("Falha ao mudar plano", { description: err?.message || "Tente novamente." });
        } finally {
            setSavingPlan(false);
        }
    };

    const saveValidity = async (isoOrNull, label) => {
        setSavingValidity(true);
        try {
            const updated = await api.patch(`/admin/tenants/${tenantId}/validity`, { trial_ends_at: isoOrNull });
            setLocalTenant((t) => ({ ...t, ...(updated || {}), trial_ends_at: (updated ? updated.trial_ends_at : isoOrNull) }));
            toast.success("Validade atualizada", { description: label || (isoOrNull ? fmtDateShort(isoOrNull) : "Sem expiração") });
            onTenantUpdated?.();
        } catch (err) {
            toast.error("Falha ao atualizar validade", { description: err?.message || "Tente novamente." });
        } finally {
            setSavingValidity(false);
        }
    };

    // +N dias a partir da validade atual (se ainda no futuro) ou de hoje.
    const extendValidity = (days) => {
        const cur = localTenant.trial_ends_at ? new Date(localTenant.trial_ends_at) : null;
        const base = cur && !Number.isNaN(cur.getTime()) && cur.getTime() > Date.now() ? cur : new Date();
        const next = new Date(base);
        next.setDate(next.getDate() + days);
        saveValidity(next.toISOString(), `+${days} dias → ${fmtDateShort(next)}`);
    };

    const setValidityFromInput = (yyyymmdd) => {
        if (!yyyymmdd) return;
        // fim do dia local — "expira em 30/09" = fim de 30/09
        const d = new Date(`${yyyymmdd}T23:59:59`);
        if (Number.isNaN(d.getTime())) {
            toast.error("Data inválida");
            return;
        }
        saveValidity(d.toISOString());
    };

    const handleStatusChange = async (e) => {
        const newStatus = e.target.value;
        setSavingStatus(true);
        try {
            await api.patch(`/admin/tenants/${tenantId}`, { status: newStatus });
            setLocalTenant((t) => ({ ...t, status: newStatus }));
            toast.success("Status atualizado", { description: `Novo status: ${newStatus}` });
            onTenantUpdated?.();
        } catch (err) {
            toast.error("Falha ao mudar status", { description: err?.message || "Tente novamente." });
        } finally {
            setSavingStatus(false);
        }
    };

    const handleResetPassword = () => {
        requestConfirm({
            title: "Resetar senha do owner",
            message: `Resetar a senha de ${localTenant.owner_name || localTenant.email}? Uma nova senha temporária será gerada.`,
            confirmLabel: "Resetar",
            danger: true,
            onConfirm: async () => {
                setResettingPwd(true);
                try {
                    const res = await api.post(`/admin/tenants/${tenantId}/reset-password`, {});
                    setResetResult(res);
                    toast.success("Senha resetada", { description: "Repasse a nova senha ao dono com segurança." });
                } catch (err) {
                    toast.error("Falha ao resetar senha", { description: err?.message || "Tente novamente." });
                } finally {
                    setResettingPwd(false);
                }
            },
        });
    };

    const currentPlan = plans.find(
        (p) => (p.slug || "").toLowerCase() === (localTenant.plan || "").toLowerCase(),
    );
    // Plano admin_only (ex.: Infinity) → sem expiração: esconde os controles de data.
    const noExpiry = currentPlan?.admin_only === true;
    const trialEndsAt = localTenant.trial_ends_at || null;
    const daysLeft = trialEndsAt
        ? Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000)
        : null;

    return (
        <div data-testid="admin-tenant-detail" className="space-y-6 max-w-4xl">
            <BackButton onBack={onBack} shopName={localTenant.shop_name} />

            {/* Layer 1 — Informações Gerais */}
            <Layer icon="fa-circle-info" title="Informações Gerais" testid="detail-layer-general">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    <DetailRow label="Nome do salão" value={localTenant.shop_name} />
                    <DetailRow label="E-mail" value={localTenant.email} />
                    <DetailRow label="Plano" value={<PlanBadge plan={(localTenant.plan || "").charAt(0).toUpperCase() + (localTenant.plan || "").slice(1)} />} />
                    <DetailRow label="Status" value={<StatusBadge status={localTenant.status} />} />
                    <DetailRow label="Data de criação" value={fmtDateTime(localTenant.created_at)} />
                    <DetailRow label="Trial expira em" value={localTenant.trial_ends_at ? fmtDateTime(localTenant.trial_ends_at) : "—"} />
                    <DetailRow label="Slug" value={`kinng.app/${localTenant.shop_id}`} mono />
                </div>
            </Layer>

            {/* Layer — Plano & Validade */}
            <Layer icon="fa-crown" title="Plano & Validade" testid="detail-layer-plan">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1.5">Plano</label>
                        <select
                            data-testid="detail-plan-select"
                            value={(localTenant.plan || "").toLowerCase()}
                            onChange={handlePlanChange}
                            disabled={savingPlan}
                            className="w-full min-h-[44px] rounded-xl border border-white/10 bg-slate-950/60 px-3 text-sm text-white outline-none focus:border-indigo-500/60 disabled:opacity-50"
                        >
                            {plans.length === 0 && <option value={(localTenant.plan || "").toLowerCase()}>{localTenant.plan}</option>}
                            {plans.map((p) => (
                                <option key={p.slug} value={p.slug}>
                                    {p.name}{p.admin_only ? " · oculto" : ""}
                                </option>
                            ))}
                        </select>
                        {noExpiry && (
                            <p className="text-[11px] text-violet-300/80 mt-1.5">
                                <i className="fa-solid fa-infinity mr-1"></i>
                                Plano {currentPlan?.name || "Infinity"} — acesso sem expiração.
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-1.5">Status</label>
                        <select
                            data-testid="detail-status-select"
                            value={localTenant.status || ""}
                            onChange={handleStatusChange}
                            disabled={savingStatus}
                            className="w-full min-h-[44px] rounded-xl border border-white/10 bg-slate-950/60 px-3 text-sm text-white outline-none focus:border-indigo-500/60 disabled:opacity-50"
                        >
                            {STATUS_OPTIONS.map((o) => (
                                <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="pt-4 mt-4 border-t border-white/5">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Cobrança (Stripe)</label>
                    <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="detail-payment-status">
                        {(() => {
                            const ps = localTenant.payment_status || "ok";
                            const meta = {
                                ok: { c: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", l: "Em dia" },
                                past_due: { c: "border-red-500/40 text-red-300 bg-red-500/10", l: "Pagamento falhou (acesso bloqueado)" },
                                canceled: { c: "border-slate-500/40 text-slate-300 bg-slate-500/10", l: "Assinatura cancelada (acesso bloqueado)" },
                            }[ps] || { c: "border-slate-500/40 text-slate-400 bg-slate-500/10", l: ps };
                            return (
                                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] uppercase tracking-wider font-bold ${meta.c}`}>
                                    <i className="fa-solid fa-circle text-[7px]"></i>{meta.l}
                                </span>
                            );
                        })()}
                        {localTenant.stripe_customer_id ? (
                            <a
                                href={`https://dashboard.stripe.com/customers/${localTenant.stripe_customer_id}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="detail-stripe-customer-link"
                                className="text-indigo-300 hover:text-indigo-200 font-mono"
                            >
                                {localTenant.stripe_customer_id} <i className="fa-solid fa-arrow-up-right-from-square text-[9px]"></i>
                            </a>
                        ) : (
                            <span className="text-slate-600">sem cliente Stripe</span>
                        )}
                        {(noExpiry || (currentPlan && !currentPlan.admin_only && Number(currentPlan.price_monthly || 0) === 0)) && (
                            <span className="text-slate-600">· plano isento de cobrança</span>
                        )}
                    </div>
                </div>

                <div className="pt-4 mt-4 border-t border-white/5">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Validade (aviso de trial)</label>

                    {noExpiry ? (
                        <div
                            data-testid="detail-validity-none"
                            className="rounded-xl border border-violet-500/25 bg-violet-500/[0.06] px-4 py-3 text-sm text-violet-200"
                        >
                            <i className="fa-solid fa-infinity mr-2"></i>Sem expiração
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 flex-wrap">
                                <input
                                    key={trialEndsAt || "none"}
                                    type="date"
                                    data-testid="detail-validity-date"
                                    defaultValue={toDateInput(trialEndsAt)}
                                    onChange={(e) => setValidityFromInput(e.target.value)}
                                    disabled={savingValidity}
                                    className="min-h-[44px] rounded-xl border border-white/10 bg-slate-950/60 px-3 text-sm text-white outline-none focus:border-indigo-500/60 disabled:opacity-50"
                                />
                                <span data-testid="detail-validity-current" className="text-xs text-slate-400">
                                    {trialEndsAt ? (
                                        <>Atual: <span className="text-white">{fmtDateShort(trialEndsAt)}</span>
                                            {typeof daysLeft === "number" && (
                                                <span className={daysLeft <= 3 ? "text-red-300 ml-1" : "text-slate-500 ml-1"}>
                                                    ({daysLeft <= 0 ? "expirado" : `${daysLeft}d restantes`})
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-slate-500">Sem data definida</span>
                                    )}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                                {[7, 15, 30, 90].map((d) => (
                                    <button
                                        key={d}
                                        type="button"
                                        data-testid={`detail-validity-plus-${d}`}
                                        onClick={() => extendValidity(d)}
                                        disabled={savingValidity}
                                        className="min-h-[36px] px-3 rounded-lg border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-50"
                                    >
                                        +{d} dias
                                    </button>
                                ))}
                                {trialEndsAt && (
                                    <button
                                        type="button"
                                        data-testid="detail-validity-clear"
                                        onClick={() => saveValidity(null, "Sem expiração")}
                                        disabled={savingValidity}
                                        className="min-h-[36px] px-3 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/25 text-xs uppercase tracking-wider transition-all disabled:opacity-50"
                                    >
                                        Limpar
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                        Essa data controla o aviso de trial exibido pro cliente. Ainda não há bloqueio
                        automático de acesso — isso será ativado junto com a integração de pagamento.
                    </p>
                </div>
            </Layer>

            {/* Layer 2 — Estatísticas */}
            <Layer icon="fa-chart-simple" title="Estatísticas" testid="detail-layer-stats">
                {!stats ? (
                    <div className="text-sm text-slate-500">Carregando…</div>
                ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <StatTile icon="fa-calendar-check" label="Agendamentos" value={stats.appointments_total} />
                        <StatTile icon="fa-address-book" label="Clientes cadastrados" value={stats.clients_total} />
                        <StatTile icon="fa-sack-dollar" label="Faturamento acumulado" value={`R$ ${Number(stats.revenue_total || 0).toFixed(0)}`} color="#10b981" />
                        <StatTile
                            icon="fa-whatsapp"
                            iconStyle="brand"
                            label="WhatsApp conectado"
                            value={stats.whatsapp_connected ? "Sim" : "Não"}
                            color={stats.whatsapp_connected ? "#25D366" : "#64748b"}
                        />
                        <StatTile
                            icon="fa-robot"
                            label="Agente IA ativo"
                            value={stats.ai_agent_enabled ? "Sim" : "Não"}
                            color={stats.ai_agent_enabled ? "#818cf8" : "#64748b"}
                        />
                    </div>
                )}
            </Layer>

            {/* Layer 3 — Configurações (plano/status/validade movidos p/ "Plano & Validade") */}
            <Layer icon="fa-sliders" title="Configurações" testid="detail-layer-settings">
                <div>
                    <button
                        data-testid="detail-reset-password-btn"
                        onClick={handleResetPassword}
                        disabled={resettingPwd}
                        className="min-h-[44px] px-4 rounded-xl border border-amber-500/30 text-amber-300 hover:bg-amber-500/15 text-xs uppercase tracking-wider font-semibold transition-all disabled:opacity-50"
                    >
                        <i className="fa-solid fa-key mr-2"></i>Resetar senha do owner
                    </button>
                    {resetResult && (
                        <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm" data-testid="detail-reset-result">
                            <div className="text-amber-200 text-xs uppercase tracking-wider font-bold mb-2">
                                <i className="fa-solid fa-triangle-exclamation mr-1.5"></i>Anote agora — não será mostrada novamente
                            </div>
                            <div className="text-slate-300 text-xs mb-1">Owner: <span className="text-white">{resetResult.owner_email}</span></div>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 rounded-lg bg-slate-950/80 text-white font-mono text-sm break-all">
                                    {resetResult.new_password}
                                </code>
                                <button
                                    onClick={() => setResetResult(null)}
                                    className="shrink-0 h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"
                                    title="Fechar"
                                >
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </Layer>

            {/* Layer 4 — Atividade Recente */}
            <Layer icon="fa-clock-rotate-left" title="Atividade Recente" testid="detail-layer-activity">
                <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Últimos logins</div>
                        {!activity ? (
                            <div className="text-sm text-slate-500">Carregando…</div>
                        ) : activity.logins.length === 0 ? (
                            <div className="text-xs text-slate-600">Nenhum login registrado ainda.</div>
                        ) : (
                            <ul className="space-y-2">
                                {activity.logins.map((l) => (
                                    <li key={l.id} className="text-xs text-slate-300 flex items-center justify-between gap-2 border-b border-white/5 pb-2">
                                        <span className="truncate">{l.actor_name}</span>
                                        <span className="text-slate-500 shrink-0">{fmtDateTime(l.created_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Ações relevantes</div>
                        {!activity ? (
                            <div className="text-sm text-slate-500">Carregando…</div>
                        ) : activity.actions.length === 0 ? (
                            <div className="text-xs text-slate-600">Nenhuma ação registrada ainda.</div>
                        ) : (
                            <ul className="space-y-2">
                                {activity.actions.map((a) => (
                                    <li key={a.id} className="text-xs text-slate-300 border-b border-white/5 pb-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="truncate capitalize">{humanizeAction(a.action)}</span>
                                            <span className="text-slate-500 shrink-0">{fmtDateTime(a.created_at)}</span>
                                        </div>
                                        <div className="text-slate-500 truncate">{a.actor_name}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </Layer>

            {/* Layer 5 — Clientes do Salão */}
            <Layer icon="fa-users" title="Clientes do Salão" testid="detail-layer-clients">
                <div className="relative mb-4">
                    <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
                    <input
                        data-testid="detail-clients-search"
                        type="text"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        placeholder="Buscar por nome ou telefone…"
                        className="w-full min-h-[44px] rounded-xl border border-white/10 bg-slate-950/60 pl-9 pr-3 text-sm text-white outline-none focus:border-indigo-500/60"
                    />
                </div>

                {clientsLoading && !clientData ? (
                    <div className="text-sm text-slate-500">Carregando…</div>
                ) : (clientData?.total || 0) === 0 ? (
                    <div className="text-center py-10 text-slate-600 text-sm" data-testid="detail-clients-empty">
                        <i className="fa-solid fa-user-slash text-2xl mb-2 block opacity-40"></i>
                        {clientSearch.trim() ? "Nenhum cliente encontrado para essa busca." : "Nenhum cliente cadastrado ainda."}
                    </div>
                ) : (
                    <>
                        <div className={`space-y-1.5 transition-opacity ${clientsLoading ? "opacity-50" : ""}`}>
                            {(clientData?.items || []).map((c) => (
                                <div
                                    key={c.id}
                                    data-testid={`detail-client-row-${c.id}`}
                                    className="rounded-xl border border-white/5 bg-slate-950/40 px-3 py-2.5 flex items-center justify-between gap-3"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white truncate flex items-center gap-2">
                                            <span className="truncate">{c.name || "Sem nome"}</span>
                                            {c.active === false && (
                                                <span className="shrink-0 text-[9px] uppercase tracking-wider text-slate-500 border border-white/10 rounded px-1 py-0.5">Inativo</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-0.5 flex-wrap">
                                            {c.phone && <span><i className="fa-solid fa-phone mr-1 opacity-60"></i>{c.phone}</span>}
                                            {c.email && <span className="truncate"><i className="fa-solid fa-envelope mr-1 opacity-60"></i>{c.email}</span>}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-sm text-emerald-300 font-semibold">{fmtBRL(c.total_spent)}</div>
                                        <div className="text-[10px] text-slate-500 mt-0.5">Desde {fmtDateShort(c.created_at)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-4 text-xs text-slate-400">
                            <span data-testid="detail-clients-count">
                                {clientData.total} cliente{clientData.total !== 1 ? "s" : ""}
                                {clientData.total > CLIENTS_PAGE_SIZE && ` · página ${clientData.page} de ${Math.max(1, Math.ceil(clientData.total / clientData.page_size))}`}
                            </span>
                            {clientData.total > CLIENTS_PAGE_SIZE && (
                                <div className="flex items-center gap-2">
                                    <button
                                        data-testid="detail-clients-prev"
                                        onClick={() => setClientPage((p) => Math.max(1, p - 1))}
                                        disabled={clientData.page <= 1 || clientsLoading}
                                        className="min-h-[36px] px-3 rounded-lg border border-white/10 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <i className="fa-solid fa-chevron-left mr-1"></i>Anterior
                                    </button>
                                    <button
                                        data-testid="detail-clients-next"
                                        onClick={() => setClientPage((p) => p + 1)}
                                        disabled={clientData.page >= Math.ceil(clientData.total / clientData.page_size) || clientsLoading}
                                        className="min-h-[36px] px-3 rounded-lg border border-white/10 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Próximo<i className="fa-solid fa-chevron-right ml-1"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Layer>

            {ConfirmModalElement}
        </div>
    );
};

const BackButton = ({ onBack, shopName }) => (
    <div className="flex items-center gap-3">
        <button
            data-testid="detail-back-btn"
            onClick={onBack}
            className="h-10 w-10 rounded-xl border border-white/10 bg-slate-900/60 hover:bg-slate-900 hover:border-indigo-500/40 text-slate-300 hover:text-white transition-all flex items-center justify-center shrink-0"
            title="Voltar para a lista"
        >
            <i className="fa-solid fa-arrow-left"></i>
        </button>
        <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Detalhe do Tenant</div>
            {shopName && <h1 className="salon-heading text-2xl text-white mt-0.5">{shopName}</h1>}
        </div>
    </div>
);

const Layer = ({ icon, title, testid, children }) => (
    <section data-testid={testid} className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5">
        <div className="flex items-center gap-2 mb-4">
            <i className={`fa-solid ${icon} text-indigo-400 text-sm`}></i>
            <h2 className="text-sm font-semibold text-white">{title}</h2>
        </div>
        {children}
    </section>
);

const StatTile = ({ icon, iconStyle, label, value, color = "#6366f1" }) => (
    <div className="rounded-xl border border-white/5 bg-slate-950/40 p-4">
        <div
            className="h-8 w-8 rounded-lg flex items-center justify-center text-sm mb-2"
            style={{ background: `${color}22`, color }}
        >
            <i className={`${iconStyle === "brand" ? "fa-brands" : "fa-solid"} ${icon}`}></i>
        </div>
        <div className="salon-heading text-xl text-white">{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
    </div>
);

export default AdminTenantDetail;
